import { useState, useEffect, useRef } from 'react';
import { downloadImage, uploadImage } from '../../../utils/upload-image';
import { PLACEHOLDER_PROFILE_NO_PHOTO } from '../../../utils/placeholders';

function PhotoModal({ userId, onClose, COLLA, initialImage, onPhotoUpdated }) {
    // Si ja tenim la imatge carregada, usem-la directament
    const [profilePic, setProfilePic] = useState(initialImage !== undefined ? initialImage : null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        let isCancelled = false;

        // Si hi ha imatge inicial, la prioritzem
        if (initialImage !== undefined) {
            setProfilePic(initialImage);
            return undefined;
        }

        setProfilePic(null);
        downloadImage(userId, COLLA)
            .then(base64 => {
                if (!isCancelled) setProfilePic(base64);
            })
            .catch(() => {
                if (!isCancelled) setProfilePic(false);
            });

        return () => {
            isCancelled = true;
        };
    }, [userId, COLLA, initialImage]);

    const fileToBase64 = (file) => (
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        })
    );

    const handleSelectPhoto = () => {
        if (isUploading) return;
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event) => {
        const file = event.target.files && event.target.files[0];
        event.target.value = '';

        if (!file) return;

        setUploadError('');
        setIsUploading(true);

        try {
            const base64 = await fileToBase64(file);
            await uploadImage(base64, userId, COLLA);

            const refreshedPhoto = await downloadImage(userId, COLLA);
            const normalizedPhoto = refreshedPhoto || false;

            setProfilePic(normalizedPhoto);
            if (onPhotoUpdated) onPhotoUpdated(normalizedPhoto);
        } catch (error) {
            console.error('Error actualitzant foto de perfil:', error);
            setUploadError('No s\'ha pogut actualitzar la foto. Torna-ho a provar.');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="photo-modal-overlay" onClick={onClose}>
            <div className="photo-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="close-btn" onClick={onClose}>×</button>
                {profilePic === null ? (
                    <div className="photo-loading">Carregant...</div>
                ) : (
                    <img
                        src={profilePic || PLACEHOLDER_PROFILE_NO_PHOTO}
                        alt="Foto de perfil ampliada"
                        className="photo-large"
                    />
                )}
                <div className="photo-modal-actions">
                    <button
                        className="photo-change-btn"
                        onClick={handleSelectPhoto}
                        disabled={isUploading}
                    >
                        {isUploading ? 'Pujant foto...' : 'Canviar foto de perfil'}
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />
                </div>
                {uploadError ? (
                    <div className="photo-modal-error">{uploadError}</div>
                ) : null}
            </div>
        </div>
    );
}

export default PhotoModal;
